import React from "react";
import classnames from "classnames";
import {
    components,
    ControlProps,
    CSSObjectWithLabel,
    DropdownIndicatorProps,
    MenuProps,
    NoticeProps,
    OptionProps,
    PlaceholderProps,
    SingleValueProps,
    ValueContainerProps
} from "react-select";

import { SelectOption } from "@vahak/core-ui/dist/components/Select";
import zIndexList from "@vahak/core-ui/dist/constants/zIndexList";
import { getSelectMenuAttachConfig } from "@vahak/core-ui/dist/methods/keyboardViewport";
import { PlaceSearchResult } from "@vahak/core/dist/custom-types/location-search";
import LocationPinOutlined from "@vahak/core/dist/standard-icons/location-pin-outlined.svg";

import { getPlaceDescriptionLabel } from "./placeLabels";
import { getPlaceSearchRemainingCharCount, isPlaceSearchQueryTooShort } from "../../../methods/location-search";

export type QuoteFormFieldStyles = Record<string, string>;
type AnySelectOption = SelectOption<any>;
type PlaceSelectOption = SelectOption<PlaceSearchResult>;

export type QuoteFormLocationMessages = {
    enterLocation: string;
    noSuchLocation: string;
    getMoreCharactersLabel: (count: number) => string;
};

/** react-select defaults options to display:block — row layout must be inline. */
const QUOTE_FORM_LOCATION_OPTION_ROW_STYLE = {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10
} as const;

const QUOTE_FORM_LOCATION_MENU_STYLES = {
    menuList: (base: CSSObjectWithLabel) => ({ ...base, padding: 0 }),
    option: (base: CSSObjectWithLabel) => ({
        ...base,
        ...QUOTE_FORM_LOCATION_OPTION_ROW_STYLE
    })
};

/** Compact sheet: make the native <input> the tap target (react-select autosizes it to ~2px). */
const SHEET_NATIVE_INPUT_STYLES = {
    input: (base: CSSObjectWithLabel) => ({
        ...base,
        width: "100%",
        gridTemplateColumns: "0 minmax(2px, 1fr)",
        opacity: 1,
        visibility: "visible"
    }),
    placeholder: (base: CSSObjectWithLabel) => ({ ...base, pointerEvents: "none" }),
    singleValue: (base: CSSObjectWithLabel) => ({ ...base, pointerEvents: "none" })
};

export function getQuoteFormLocationNoOptionsMessage(inputValue: string, copy: QuoteFormLocationMessages): string {
    if (!inputValue) {
        return copy.enterLocation;
    }

    if (isPlaceSearchQueryTooShort(inputValue)) {
        return copy.getMoreCharactersLabel(getPlaceSearchRemainingCharCount(inputValue));
    }

    return copy.noSuchLocation;
}

/** Must sit above BottomSheet (compact load posting route step on mobile). */
const QUOTE_FORM_LOCATION_MENU_Z_INDEX = zIndexList.bottomSheet + 1;

/** Portal menu to body when needed (Android); iOS keeps absolute so menu tracks keyboard scroll. */
export function getQuoteFormLocationMenuConfig(portalMenu: boolean, nativeInputTapTarget = false) {
    const attach = getSelectMenuAttachConfig(portalMenu);
    const isPortaled = attach.menuPosition === "fixed";
    const locationStyles = nativeInputTapTarget
        ? { ...QUOTE_FORM_LOCATION_MENU_STYLES, ...SHEET_NATIVE_INPUT_STYLES }
        : QUOTE_FORM_LOCATION_MENU_STYLES;

    return {
        ...attach,
        styles: isPortaled
            ? {
                  ...locationStyles,
                  menuPortal: (base: CSSObjectWithLabel) => ({
                      ...base,
                      zIndex: QUOTE_FORM_LOCATION_MENU_Z_INDEX
                  }),
                  menu: (base: CSSObjectWithLabel) => ({
                      ...base,
                      zIndex: base.zIndex ?? QUOTE_FORM_LOCATION_MENU_Z_INDEX
                  })
              }
            : locationStyles
    };
}

export const hiddenMultiValue = () => null;

export function filterOptionByLabel(option: { label: string }, inputValue: string) {
    return option.label.toLowerCase().includes(inputValue.trim().toLowerCase());
}

export function renderHighlightedText(text: string, query: string, highlightClassName: string): React.ReactNode {
    if (!text || !query?.trim()) {
        return text;
    }

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${escapedQuery})`, "gi"));

    return parts.map((part, index) => {
        if (part.toLowerCase() === query.toLowerCase()) {
            return (
                <span key={`${part}-${index}`} className={highlightClassName}>
                    {part}
                </span>
            );
        }

        return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
    });
}

export function createQuoteFormLocationControl(
    Styles: QuoteFormFieldStyles,
    icon: React.ReactNode,
    options?: { allowNativeTouchFocus?: boolean }
) {
    return ({ children, className, innerProps, ...props }: ControlProps<AnySelectOption, boolean>) => {
        const { onMouseDown: _onMouseDown, onTouchEnd: _onTouchEnd, ...restInnerProps } = innerProps;

        return (
            <components.Control
                {...props}
                innerProps={options?.allowNativeTouchFocus ? restInnerProps : innerProps}
                className={classnames(
                    className,
                    "select-control",
                    props.selectProps.isSearchable && "select-control--searchable",
                    Styles.locationSelectControl
                )}
            >
                <span className={Styles.fieldIconWrap} aria-hidden>
                    {icon}
                </span>
                {children}
            </components.Control>
        );
    };
}

export function createQuoteFormLorryControl(Styles: QuoteFormFieldStyles, icon: React.ReactNode) {
    return ({ children, className, ...props }: ControlProps<AnySelectOption, boolean>) => (
        <components.Control
            {...props}
            className={classnames(
                className,
                "select-control",
                Styles.locationSelectControl,
                Styles.lorrySelectControl,
                props.selectProps.menuIsOpen && Styles.lorrySelectControlOpen
            )}
        >
            <span className={Styles.fieldIconWrap} aria-hidden>
                {icon}
            </span>
            {children}
        </components.Control>
    );
}

export function createQuoteFormChevronIndicator(Styles: QuoteFormFieldStyles, icon: React.ReactNode) {
    return (props: DropdownIndicatorProps<AnySelectOption, boolean>) => (
        <components.DropdownIndicator {...props} className={Styles.selectChevronWrap}>
            <span className={classnames(Styles.chevronIcon, props.selectProps.menuIsOpen && Styles.chevronIconOpen)}>
                {icon}
            </span>
        </components.DropdownIndicator>
    );
}

export function createQuoteFormLocationPlaceholder(Styles: QuoteFormFieldStyles) {
    return ({ children, className, ...props }: PlaceholderProps<AnySelectOption, boolean>) => (
        <components.Placeholder
            {...props}
            className={classnames(className, "select-placeholder", Styles.fieldPlaceholder)}
        >
            {children}
        </components.Placeholder>
    );
}

export function createQuoteFormLocationSingleValue(Styles: QuoteFormFieldStyles) {
    return ({ children, className, ...props }: SingleValueProps<AnySelectOption, boolean>) => (
        <components.SingleValue {...props} className={classnames(className, "select-singleValue", Styles.fieldValue)}>
            {children}
        </components.SingleValue>
    );
}

export function createQuoteFormLocationInput(options?: { alwaysVisible?: boolean }) {
    return (props: React.ComponentProps<typeof components.Input>) => {
        const { className, isHidden, ...rest } = props;

        return (
            <components.Input
                {...rest}
                isHidden={options?.alwaysVisible ? false : isHidden}
                className={classnames(className, "select-input")}
            />
        );
    };
}

export function createQuoteFormLocationMenu(Styles: QuoteFormFieldStyles, requireInput = true) {
    return ({ className, placement, ...props }: MenuProps<AnySelectOption, boolean>) => {
        if (requireInput && !props.selectProps.inputValue) {
            return null;
        }

        return (
            <components.Menu
                {...props}
                placement={placement}
                className={classnames(
                    className,
                    Styles.locationDropdownMenu,
                    placement === "top" && Styles.locationDropdownMenuTop
                )}
            />
        );
    };
}

export function createQuoteFormLocationOption(Styles: QuoteFormFieldStyles) {
    return (props: OptionProps<PlaceSelectOption, false>) => {
        const { data, isFocused, isSelected, selectProps } = props;
        const inputValue = String(selectProps.inputValue || "");
        const descriptionLabel = getPlaceDescriptionLabel(data.value);
        const isActive = isFocused || isSelected;

        return (
            <components.Option
                {...props}
                className={classnames(Styles.locationOption, isActive && Styles.locationOptionActive)}
            >
                <LocationPinOutlined
                    className={classnames(Styles.locationOptionIcon, isActive && Styles.locationOptionIconActive)}
                    aria-hidden
                />
                <span className={Styles.locationOptionText}>
                    {renderHighlightedText(descriptionLabel, inputValue, Styles.locationMatch)}
                </span>
            </components.Option>
        );
    };
}

export function createQuoteFormLocationSelectComponents(
    Styles: QuoteFormFieldStyles,
    options: {
        icon: React.ReactNode;
        getNoOptionsMessage: (inputValue: string) => string;
        /** Compact sheet: keep the native input visible so iOS will show caret + keyboard. */
        revealNativeInput?: boolean;
    }
) {
    return {
        DropdownIndicator: () => null,
        Menu: createQuoteFormLocationMenu(Styles),
        NoOptionsMessage: createQuoteFormNoOptionsMessage(Styles, options.getNoOptionsMessage),
        Option: createQuoteFormLocationOption(Styles),
        Control: createQuoteFormLocationControl(Styles, options.icon, {
            allowNativeTouchFocus: options.revealNativeInput
        }),
        Input: createQuoteFormLocationInput({ alwaysVisible: options.revealNativeInput }),
        Placeholder: createQuoteFormLocationPlaceholder(Styles),
        SingleValue: createQuoteFormLocationSingleValue(Styles)
    };
}

export function createQuoteFormNoOptionsMessage(
    Styles: QuoteFormFieldStyles,
    getMessage: (inputValue: string) => string
) {
    return (props: NoticeProps<AnySelectOption, boolean>) => (
        <components.NoOptionsMessage
            {...props}
            className={classnames("select-noOptionMessage", Styles.locationNoOptionsMessage)}
        >
            {getMessage(String(props.selectProps.inputValue || ""))}
        </components.NoOptionsMessage>
    );
}

export function createQuoteFormMultiValueContainer(Styles: QuoteFormFieldStyles, displayValue: string) {
    return (props: ValueContainerProps<AnySelectOption, true>) => {
        if (!displayValue) {
            return <components.ValueContainer {...props} />;
        }

        return (
            <components.ValueContainer {...props}>
                <span className={classnames(Styles.fieldValue, Styles.multiFieldValue)} title={displayValue}>
                    {displayValue}
                </span>
                {React.Children.map(props.children, (child) => {
                    if (!React.isValidElement(child)) {
                        return child;
                    }

                    return child.type === components.MultiValue ? null : child;
                })}
            </components.ValueContainer>
        );
    };
}
